import React, { useState, useMemo } from "react";
import { Phone, Mail, MapPin, Wrench, Trash2, Search, Inbox, Loader2, Download } from "lucide-react";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const STATUSES = ["new", "contacted", "quoted", "won", "lost"];
const STATUS_STYLE = {
  new: "bg-brand-sky/15 text-brand-ocean ring-brand-sky/40",
  contacted: "bg-amber-100 text-amber-700 ring-amber-300",
  quoted: "bg-purple-100 text-purple-700 ring-purple-300",
  won: "bg-green-100 text-green-700 ring-green-300",
  lost: "bg-red-100 text-red-600 ring-red-300",
};

export default function LeadsView({ leads, loading, onStatus, onDelete }) {
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    let list = leads || [];
    if (tab !== "all") list = list.filter((l) => l.type === tab);
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter((l) => [l.name, l.email, l.phone, l.service, l.message, l.address].filter(Boolean).some((v) => String(v).toLowerCase().includes(s)));
    }
    return list;
  }, [leads, tab, q]);

  const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return iso; } };

  const exportCsv = () => {
    const cols = ["type", "status", "name", "email", "phone", "address", "service", "message", "created_at"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [cols.join(","), ...filtered.map((l) => cols.map((c) => esc(l[c])).join(","))];
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "miller-leads.csv"; a.click();
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
        <div className="flex gap-2">
          {[["all", "All"], ["estimate", "Estimates"], ["contact", "Contacts"]].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} className={`px-4 h-10 rounded-lg text-sm font-heading font-bold transition-colors ${tab === k ? "bg-brand-ocean text-white" : "bg-white text-brand-navy ring-1 ring-brand-mist hover:ring-brand-sky/40"}`}>{label}</button>
          ))}
        </div>
        <div className="flex gap-2">
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-slate" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search leads..." className="pl-9 h-10 bg-white" />
          </div>
          <button onClick={exportCsv} className="h-10 px-4 rounded-lg bg-white ring-1 ring-brand-mist hover:ring-brand-ocean text-brand-navy font-heading font-bold text-sm flex items-center gap-2 transition-all"><Download className="h-4 w-4" /> CSV</button>
        </div>
      </div>

      <div className="mt-5 space-y-3">
        {loading && filtered.length === 0 && <div className="text-center py-16"><Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-ocean" /></div>}
        {!loading && filtered.length === 0 && (
          <div className="text-center py-16 rounded-2xl bg-white ring-1 ring-brand-mist">
            <Inbox className="h-10 w-10 text-brand-slate/40 mx-auto" /><p className="mt-3 text-brand-slate font-medium">No leads found.</p>
          </div>
        )}
        {filtered.map((l) => (
          <div key={l.id} className="rounded-2xl bg-white p-5 ring-1 ring-brand-mist shadow-card">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] font-heading font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-full ${l.type === "estimate" ? "bg-brand-gold/20 text-brand-gold" : "bg-brand-sky/15 text-brand-ocean"}`}>{l.type}</span>
                  <h3 className="font-heading font-extrabold text-brand-navy text-lg">{l.name}</h3>
                  <span className="text-xs text-brand-slate">{fmtDate(l.created_at)}</span>
                </div>
                <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-brand-slate">
                  {l.phone && <a href={`tel:${l.phone}`} className="flex items-center gap-1.5 hover:text-brand-ocean"><Phone className="h-4 w-4" /> {l.phone}</a>}
                  {l.email && <a href={`mailto:${l.email}`} className="flex items-center gap-1.5 hover:text-brand-ocean break-all"><Mail className="h-4 w-4" /> {l.email}</a>}
                  {l.address && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {l.address}</span>}
                  {l.service && <span className="flex items-center gap-1.5"><Wrench className="h-4 w-4" /> {l.service}</span>}
                </div>
                {l.message && <p className="mt-3 text-sm text-brand-navy/80 bg-brand-mist/60 rounded-lg px-3 py-2 whitespace-pre-wrap">{l.message}</p>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`hidden sm:inline text-[11px] font-heading font-bold uppercase px-2.5 py-1 rounded-full ring-1 ${STATUS_STYLE[l.status] || STATUS_STYLE.new}`}>{l.status || "new"}</span>
                <Select value={l.status || "new"} onValueChange={(v) => onStatus(l, v)}>
                  <SelectTrigger className="h-9 w-32 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                </Select>
                <button onClick={() => onDelete(l)} aria-label="Delete lead" className="h-9 w-9 grid place-items-center rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
